import { Axis, Camera, Matrix, Observer, Quaternion, Ray, TmpVectors, UniversalCamera, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { Layers, ZedCanvas } from '@modules/core';
import { useZed } from '@modules/core/src/react/useZed';
import { toRadian } from '@modules/standard';
import { StandardModule } from '@modules/standard/src/StandardModule';
import zams from '@zams/logger/zed';
import { PricePolicy } from '@zigbang/utils';
import { Icon } from '@zigbang/zuix2';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TourDanjiCameraViewType } from '..';
import { LayerMask } from '../Constants';
import { UnitResponse } from '../ecs/component/response/Unit';
import { shouldShowPresalePrice } from '../ecs/system/UnitPriceSystem';
import { getTourDanjiZamsParams, TourDanjiModule } from '../TourDanjiModule';
import { NO_PRICE_NOTICE, supplyTypeNotice } from '../priceNotice';

import ExpandIcon from './assets/expand.png';
import {
  GUIDE_3D_TOUR_LINES,
  GUIDE_3D_TOUR_TITLE,
  GUIDE_ZIGBANG_PRICE_LINES,
  GUIDE_ZIGBANG_PRICE_TITLE,
  GuideSection,
} from './GuideContents';
import { useScreen } from './hooks/useScreen';
import { InfoTooltip } from './InfoTooltip';
import styles from './styles/InteriorTourPanel.module.css';

export enum InteriorTourPanelState {
  Inactive,
  FirstPerson,
  Overview,
}

const stateClass = {
  [InteriorTourPanelState.Inactive]: '',
  [InteriorTourPanelState.FirstPerson]: styles.firstPerson,
  [InteriorTourPanelState.Overview]: styles.overview,
};

type WindowPoint = { name: string; matrix: Matrix };

export function InteriorTourPanel() {
  const zed = useZed<[BabylonjsModule, StandardModule, TourDanjiModule]>();
  const { isDesktop } = useScreen();
  const states = useRef<InteriorTourPanelState[]>([]);
  const [state, _setState] = useState(InteriorTourPanelState.Inactive);
  const [nextState, _setNextState] = useState(InteriorTourPanelState.Inactive);
  const [camera, setCamera] = useState<UniversalCamera>();
  const [unitResponse, setUnitResponse] = useState<UnitResponse>();
  const [minimapCanvas, setMinimapCanvas] = useState<HTMLCanvasElement | null>();
  const [cameraMinimapMatrix, setCameraMinimapMatrix] = useState<Matrix>();
  const [windowPoints, setWindowPoints] = useState<WindowPoint[]>();
  const [windowPoint, setWindowPoint] = useState<WindowPoint>();
  const [areaUnit, setAreaUnit] = useState<'m2' | 'p'>('m2');
  const [activeFloor, setActiveFloor] = useState(0);
  const [currentFloor, setCurrentFloor] = useState(0);
  const [priceInfo, setPriceInfo] = useState<{
    type: '분양가' | '직방시세' | undefined;
    price: number | undefined;
    formattedPrice: string | undefined;
    label: string;
  }>();
  const [isHideUnitName, setIsHideUnitName] = useState(false);

  const getZamsParams = useCallback(() => {
    return {
      ...getTourDanjiZamsParams(),
      zed_dong: unitResponse?.buildingName ?? '',
      zed_ho: unitResponse?.unitName ?? '',
    };
  }, [unitResponse]);

  const setNextState = useCallback(
    (newState: InteriorTourPanelState) => {
      if (newState === state) return;
      states.current.push(state);
      _setNextState(newState);
    },
    [state, _setNextState]
  );

  const back = useCallback(() => {
    const prevState = states.current.pop();
    const newState = prevState ?? InteriorTourPanelState.Inactive;
    if (newState === InteriorTourPanelState.Inactive) {
      zams
        .z141({
          ...getZamsParams(),
        })
        .send();
    } else if (newState === InteriorTourPanelState.FirstPerson) {
      zams
        .z146({
          ...getZamsParams(),
        })
        .send();
    }
    changeCurrentFloor(activeFloor);
    _setNextState(newState);
  }, [_setNextState, activeFloor, currentFloor, getZamsParams]);

  const setState = useCallback((newState: InteriorTourPanelState) => {
    states.current.pop();
    _setNextState(newState);
  }, []);

  const showOverview = useCallback(() => {
    setNextState(InteriorTourPanelState.Overview);
    zams
      .z143({
        ...getZamsParams(),
      })
      .send();
  }, [setNextState, getZamsParams]);

  const selectWindow = useCallback(
    (windowPoint: WindowPoint) => {
      setActiveFloor(currentFloor);
      setWindowPoint(windowPoint);
      setState(InteriorTourPanelState.FirstPerson);
      zams.z148(getZamsParams()).send();
    },
    [currentFloor, getZamsParams]
  );

  const projectToMinimap = useCallback(
    (matrix: Matrix) => {
      if (!camera) throw new Error('Camera is not initialized');
      if (!minimapCanvas) throw new Error('Minimap canvas is not initialized');

      const transformMatrix = camera.getTransformationMatrix();

      const forward = TmpVectors.Vector3[0];
      const viewForward = TmpVectors.Vector3[1];
      const viewPosition = TmpVectors.Vector3[2];
      const screenRotation = TmpVectors.Quaternion[1];
      const screenPosition = TmpVectors.Vector3[3];

      Vector3.TransformNormalToRef(Vector3.LeftHandedForwardReadOnly, matrix, forward);
      Vector3.TransformNormalToRef(forward, transformMatrix, viewForward);
      const bearing = Math.atan2(viewForward.x, viewForward.y) + Math.PI;
      Quaternion.RotationAxisToRef(Axis.Z, bearing, screenRotation);

      Vector3.TransformCoordinatesToRef(matrix.getTranslation(), transformMatrix, viewPosition);

      const screenX = ((1 + viewPosition.x) / 2) * minimapCanvas.clientWidth;
      const screenY = ((1 - viewPosition.y) / 2) * minimapCanvas.clientHeight;
      const screenMatrix = Matrix.Compose(
        Vector3.OneReadOnly,
        screenRotation,
        screenPosition.copyFromFloats(screenX, screenY, 0)
      );
      return screenMatrix;
    },
    [camera, minimapCanvas]
  );

  const toggleAreaUnit = useCallback(() => {
    zams
      .z147({
        ...getZamsParams(),
        status_value: areaUnit === 'm2' ? '평' : 'm',
      })
      .send();
    setAreaUnit((unit) => (unit === 'm2' ? 'p' : 'm2'));
  }, [areaUnit, getZamsParams]);

  const handleGuideToggle = useCallback(
    (open: boolean) => {
      zams
        .z144({
          ...getZamsParams(),
          status: open ? '1' : '0',
        })
        .send();
    },
    [getZamsParams]
  );

  const changeCurrentFloor = useCallback(
    (newFloor: number) => {
      if (currentFloor === newFloor) return;
      setCurrentFloor(newFloor);
      zed?.api.changeUnitFloor(newFloor);
    },
    [currentFloor]
  );

  useEffect(() => {
    if (!zed || state !== InteriorTourPanelState.Inactive) return;
    let onCameraViewTypeChanged: Observer<TourDanjiCameraViewType>;
    onCameraViewTypeChanged = zed.api.observer.onCameraViewTypeChanged.add((data) => {
      if (data !== 'UnitWindowView' && data !== 'FakeUnitWindowView') return;
      setNextState(InteriorTourPanelState.FirstPerson);
    });
    return () => {
      onCameraViewTypeChanged?.remove();
    };
  }, [zed, state]);

  useEffect(() => {
    if (!zed) return;
    const onUnitActivated = zed.api.observer.onUnitActivated.add((data) => {
      setUnitResponse(data);
      if (data) {
        zams
          .z140({
            ...getTourDanjiZamsParams(),
            zed_dong: data.buildingName ?? '',
            zed_ho: data.unitName ?? '',
          })
          .send();
      }
    });

    const onUnitViewStateChanged = zed.api.observer.onUnitViewStateChanged.add(({ isHideUnitName }) => {
      setIsHideUnitName(isHideUnitName ?? false);
    });

    return () => {
      onUnitActivated.remove();
      onUnitViewStateChanged.remove();
    };
  }, [zed]);

  useEffect(() => {
    if (!unitResponse) return;

    const { unitMeshes: meshes, floorIndex, windowPoints } = unitResponse;
    const mesh = meshes?.[floorIndex];
    if (!mesh) return;
    if (!windowPoints?.some((w) => w !== undefined)) return;

    const newCamera = new UniversalCamera('UnitOverviewTopDown', Vector3.ZeroReadOnly);

    setCamera(newCamera);

    return () => {
      newCamera.dispose();
      setCamera(undefined);
    };
  }, [unitResponse]);

  useEffect(() => {
    if (!unitResponse) return;

    const { floorIndex } = unitResponse;

    setActiveFloor(floorIndex);
    setCurrentFloor(floorIndex);

    return () => {
      setActiveFloor(0);
      setCurrentFloor(0);
    };
  }, [unitResponse]);

  useEffect(() => {
    if (!unitResponse || !camera) return;

    const { unitMeshes } = unitResponse;
    const mesh = unitMeshes?.[currentFloor];
    if (!mesh) return;

    const meshes = mesh.getChildMeshes();
    meshes.forEach((mesh) => {
      mesh.layerMask |= LayerMask.Minimap;
    });

    const extent = mesh.getHierarchyBoundingVectors();
    const size = extent.max.subtract(extent.min);
    const halfSize = size.scale(0.5);
    const maxHalfSize = Math.max(halfSize.x, halfSize.z);
    const center = extent.min.add(halfSize);

    camera.position = center.subtract(new Vector3(0, halfSize.y + 2, 0));
    camera.rotation = new Vector3(-90 * toRadian, 0, 0);
    camera.layerMask = LayerMask.Minimap;
    camera.mode = Camera.ORTHOGRAPHIC_CAMERA;
    camera.orthoLeft = -maxHalfSize;
    camera.orthoTop = -maxHalfSize;
    camera.orthoRight = maxHalfSize;
    camera.orthoBottom = maxHalfSize;
    camera.maxZ = halfSize.y + 4;

    return () => {
      meshes.forEach((mesh) => {
        mesh.layerMask &= ~LayerMask.Minimap;
      });
    };
  }, [unitResponse, camera, currentFloor]);

  useEffect(() => {
    if (state === nextState) return;
    _setState(nextState);
    if (nextState === InteriorTourPanelState.Inactive) {
      zed?.api.exitUnit();
    }
  }, [zed, nextState]);

  useEffect(() => {
    if (!zed || state !== InteriorTourPanelState.Inactive) return;
    setWindowPoint(undefined);
  }, [zed, state]);

  useEffect(() => {
    if (!zed || state !== InteriorTourPanelState.FirstPerson) return;

    const cameraAPI = zed.api['_controller']['cameraAPI'];

    if (windowPoint) {
      windowPoint.matrix.decompose(undefined, TmpVectors.Quaternion[0], TmpVectors.Vector3[0]);

      const backward = Vector3.TransformNormalToRef(
        Vector3.LeftHandedBackwardReadOnly,
        windowPoint.matrix,
        TmpVectors.Vector3[2]
      ).normalize();

      const RAY_MAX_DISTANCE = 2.5;
      const ray = new Ray(TmpVectors.Vector3[0], backward, RAY_MAX_DISTANCE);

      const pickResult = zed.api.scene.pickWithRay(ray, (mesh) => {
        if (mesh.layerMask === LayerMask.Default) return false;
        return (mesh.layerMask & LayerMask.UnitCollider) === LayerMask.UnitCollider;
      });

      const offset = pickResult?.distance ? pickResult.distance - 0.3 : RAY_MAX_DISTANCE;
      const position = TmpVectors.Vector3[0].addInPlace(backward.scale(offset));
      const rotation = TmpVectors.Quaternion[0].toEulerAnglesToRef(TmpVectors.Vector3[1]);
      rotation.y += Math.PI;

      cameraAPI.camera.target.copyFrom(position);
      cameraAPI.camera.setRotation(rotation, false, true);
    }
  }, [zed, state]);

  useEffect(() => {
    if (!zed || !unitResponse || state !== InteriorTourPanelState.Overview) return;

    setWindowPoint(undefined);

    const { windowPoints } = unitResponse;
    setWindowPoints(
      windowPoints?.[currentFloor]?.map((point) => {
        return { name: point.name, matrix: point.transform.getWorldMatrix() };
      })
    );

    return () => {
      setWindowPoints(undefined);
    };
  }, [zed, unitResponse, currentFloor, state]);

  useEffect(() => {
    if (!zed || !camera || !minimapCanvas) return;

    const cameraAPI = zed.api['_controller']['cameraAPI'];

    const observable = cameraAPI.camera.onProjectionMatrixChangedObservable.add((mainCamera) => {
      const matrix = mainCamera.getWorldMatrix();
      const screenMatrix = projectToMinimap(matrix);
      setCameraMinimapMatrix(screenMatrix);
    });

    return () => {
      observable.remove();
    };
  }, [zed, camera, minimapCanvas, projectToMinimap]);

  useEffect(() => {
    if (!unitResponse) return;

    const showPresalePrice = shouldShowPresalePrice(unitResponse.unitDetail.서비스구분!);

    let type: '분양가' | '직방시세' | undefined;
    let price;
    let formattedPrice;
    let label = '정보없음';

    // HGNN-14295: 분양가 노출 단지는 공급유형별 매트릭스(§5.1)를 적용한다.
    // '분양'만 가격을 노출하고(호별→룸타입→분양가미확인), 그 외 공급유형은 문구로 대체한다(P-3DHO-001).
    const 공급유형 = unitResponse.unitDetail.공급유형;
    if (!unitResponse.unitDetail.서비스구분) {
      type = undefined;
    } else if (showPresalePrice) {
      if (공급유형 && 공급유형 !== '분양') {
        // 비-분양(조합·구분불가·임대·기타): 가격 미노출, 공급유형 문구
        label = supplyTypeNotice[공급유형] ?? '정보없음';
      } else {
        // 분양(또는 공급유형 불명): 호별 → 룸타입 → 분양가미확인
        const 분양가 = unitResponse.unitDetail.roomTypeInfo.분양가;
        if (분양가 && 분양가.ho분양가 > 0) {
          type = '분양가';
          price = 분양가.ho분양가;
          formattedPrice = PricePolicy.markerPricePolicy(price);
          label = `분양가 ${formattedPrice}`;
        } else if (분양가 && 분양가.roomType분양가 > 0) {
          type = '분양가';
          price = 분양가.roomType분양가;
          formattedPrice = PricePolicy.markerPricePolicy(price);
          label = `분양가 ${formattedPrice}`;
        } else {
          label = NO_PRICE_NOTICE;
        }
      }
    } else if (unitResponse.unitDetail.zigbangPrice && unitResponse.unitDetail.zigbangPrice?.trade.price > 0) {
      type = '직방시세';
      price = unitResponse.unitDetail.zigbangPrice.trade.price;
      formattedPrice = PricePolicy.markerPricePolicy(price);
      label = `직방시세 매매 ${formattedPrice}`;
    }

    setPriceInfo({
      type,
      price,
      formattedPrice,
      label,
    });

    return () => {
      setPriceInfo(undefined);
    };
  }, [unitResponse]);

  useEffect(() => {
    if (state === InteriorTourPanelState.Overview) {
      zams
        .z145({
          ...getZamsParams(),
        })
        .send();
    }
  }, [state]);

  return (
    unitResponse &&
    state !== InteriorTourPanelState.Inactive && (
      <div style={{ zIndex: Layers.UI_CONTENTS }} className={`${styles.root} ${stateClass[state]}`}>
        <header>
          <Icon
            shape="Back"
            onPress={back}
            color="white"
            style={{
              position: 'absolute',
              width: '3em',
              height: '3em',
              top: '3em',
              left: '2em',
              pointerEvents: 'auto',
            }}
          />
          <div className={styles.titleRow}>
            <InfoTooltip
              ariaLabel="3D단지투어 및 직방시세 안내"
              onToggle={handleGuideToggle}
              iconSize={isDesktop ? 36 : 24}>
              <GuideSection title={GUIDE_3D_TOUR_TITLE} lines={GUIDE_3D_TOUR_LINES} />
              <GuideSection title={GUIDE_ZIGBANG_PRICE_TITLE} lines={GUIDE_ZIGBANG_PRICE_LINES} />
            </InfoTooltip>
            <h2>{`${unitResponse.buildingName}${!isHideUnitName ? ` ${unitResponse.unitName}` : ''}`}</h2>
          </div>
          {priceInfo && <p className={styles.priceInfo}>{priceInfo.label}</p>}
          {false && <p>이 집은 매물로 나와 있지 않아요</p>}
          <button className={styles.unitToggle} onClick={toggleAreaUnit}>
            <span className={areaUnit == 'p' ? styles.active : undefined}>평</span>
            <span className={areaUnit == 'm2' ? styles.active : undefined}>㎡</span>
          </button>
        </header>
        {state === InteriorTourPanelState.Overview && (
          <>
            <dl className={styles.specs}>
              {unitResponse.unitDetail.roomTypeInfo.공급면적 && (
                <>
                  <dt>면적(공급/전용)</dt>
                  <dd>
                    {unitResponse.unitDetail.roomTypeInfo.공급면적[areaUnit]}
                    {unitResponse.unitDetail.roomTypeInfo.전용면적 && (
                      <>
                        /{unitResponse.unitDetail.roomTypeInfo.전용면적[areaUnit]}
                        {{ m2: '㎡', p: '평' }[areaUnit]}
                      </>
                    )}
                  </dd>
                </>
              )}
              <dt>방/욕실</dt>
              <dd>
                {unitResponse.unitDetail.roomTypeInfo.방수}/{unitResponse.unitDetail.roomTypeInfo.욕실수}개
              </dd>
              <dt>현관구조</dt>
              <dd>{unitResponse.unitDetail.roomTypeInfo.현관구조}</dd>
            </dl>
          </>
        )}
        {camera && (
          <div className={`${styles.minimapContainerWrapper} ${styles.visible}`}>
            <aside className={styles.minimapContainer}>
              <svg className={styles.compass} viewBox="0 0 100 100">
                <defs>
                  <mask id="circleMask">
                    <rect x="0" y="0" width="100" height="100" fill="white" />
                    <rect x="45" y="0" width="10" height="100" fill="black" />
                    <rect x="0" y="45" width="100" height="10" fill="black" />
                  </mask>
                </defs>
                <circle cx="50" cy="50" r="50" mask="url(#circleMask)" />
              </svg>
              <p
                className={styles.bearing}
                style={{
                  top: '50%',
                  left: '100%',
                }}
                data-label="동"></p>
              <p
                className={styles.bearing}
                style={{
                  top: '50%',
                  left: 0,
                }}
                data-label="서"></p>
              <p
                className={styles.bearing}
                style={{
                  top: '100%',
                  left: '50%',
                }}
                data-label="남"></p>
              <p
                className={styles.bearing}
                style={{
                  color: 'red',
                  top: 0,
                  left: '50%',
                }}
                data-label="북"></p>
              <div className={styles.minimap}>
                <ZedCanvas
                  ref={setMinimapCanvas}
                  options={{
                    targetFps: 3,
                    scale: window.devicePixelRatio ?? 1,
                    camera,
                  }}
                  touch-action="none"
                />
                {windowPoints && (
                  <ul className={styles.windowPoints}>
                    {windowPoints.map((point) => {
                      const screenMatrix = projectToMinimap(point.matrix);
                      return (
                        <li
                          key={point.name}
                          style={{
                            transform: `matrix3d(${screenMatrix.asArray().join(',')})`,
                          }}
                          onClick={() => selectWindow(point)}
                        />
                      );
                    })}
                  </ul>
                )}
                {cameraMinimapMatrix && activeFloor === currentFloor && (
                  <div
                    className={styles.cameraFrustum}
                    style={{
                      transform: `matrix3d(${cameraMinimapMatrix.asArray().join(',')})`,
                    }}
                  />
                )}
              </div>
              {unitResponse.unitMetadata.층갯수 > 1 && (
                <ul className={styles.floors}>
                  {Array.from({ length: unitResponse.unitMetadata.층갯수 }, (_, f) => (
                    <li key={f}>
                      <input
                        name="floors"
                        id={`Floor${f}`}
                        type="radio"
                        value={f}
                        onChange={(e) => {
                          changeCurrentFloor(parseInt(e.target.value));
                        }}
                        checked={f === currentFloor}
                      />
                      <label htmlFor={`Floor${f}`}>{f + 1}</label>
                    </li>
                  ))}
                </ul>
              )}
              <button className={styles.showOverview} onClick={showOverview}>
                <img src={ExpandIcon} className={styles.expandIcon} />
              </button>
            </aside>
          </div>
        )}
        {!camera && (
          <div className={`${styles.minimapContainerWrapper} ${styles.visible}`}>
            <aside className={styles.minimapEmptyNoticeContainer}>
              <div className={styles.minimapEmptyNotice}>{'평면도 정보가 없습니다'}</div>
            </aside>
          </div>
        )}
      </div>
    )
  );
}
